import React, { useState, useEffect, useCallback } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import config from "../../Config";
import TransportMenu from "./TransportMenu";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import { scrollToTop } from "../Shared/ScrollOnTop";

/*
 * The bus fleet.
 *
 * A vehicle is a physical bus and is deliberately separate from a route: several routes
 * are served by the same bus. tblBusRoots still drives the fee posting exactly as before
 * and simply points here through Vehicle_ID, so nothing on this screen can affect fees.
 */
function ManageVehicles() {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const UserName = localStorage.getItem("UserName");
  const MemberType = localStorage.getItem("MemberType");

  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [search, setSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);

  // driver assignment dialog
  const [showAssign, setShowAssign] = useState(false);
  const [assignVehicle, setAssignVehicle] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [assignStaffId, setAssignStaffId] = useState("");
  const [assignFrom, setAssignFrom] = useState(moment().format("YYYY-MM-DD"));
  const [assignSaving, setAssignSaving] = useState(false);

  const auth = useCallback(
    (extra) => ({
      AccessKey,
      UserID,
      UserName,
      MemberType: MemberType || "Admin",
      SchoolID: 1,
      ...extra,
    }),
    [AccessKey, UserID, UserName, MemberType]
  );

  const post = useCallback(
    (url, data) =>
      axios({
        method: "post",
        url: config.base_url + url,
        headers: { "Content-Type": "application/json" },
        data,
      }),
    []
  );

  const loadVehicles = useCallback(() => {
    setLoading(true);
    post("Vehicle/GetVehicles", auth({ Name: search, Is_Active: activeOnly }))
      .then((res) => {
        if (res.data.status_code === 1) {
          setVehicles(res.data.ListofRecords || []);
        } else {
          setVehicles([]);
          toast.error(res.data.status_message || "Could not load vehicles", {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(() =>
        toast.error("Network error", { position: toast.POSITION.TOP_RIGHT })
      )
      .finally(() => setLoading(false));
  }, [post, auth, search, activeOnly]);

  useEffect(() => {
    document.title = "Manage Vehicles";
    scrollToTop();
  }, []);

  useEffect(() => {
    loadVehicles();
  }, [loadVehicles]);

  const openAssign = (vehicle) => {
    setAssignVehicle(vehicle);
    setAssignStaffId("");
    setAssignFrom(moment().format("YYYY-MM-DD"));
    setShowAssign(true);

    post("Vehicle/GetAvailableDrivers", auth({}))
      .then((res) => {
        if (res.data.status_code === 1) setDrivers(res.data.ListofRecords || []);
      })
      .catch(() => setDrivers([]));
  };

  const saveAssignment = () => {
    if (!assignStaffId) {
      toast.error("Select a driver", { position: toast.POSITION.TOP_RIGHT });
      return;
    }
    setAssignSaving(true);
    post(
      "Vehicle/AssignDriver",
      auth({
        Vehicle_ID: assignVehicle.Vehicle_ID,
        Staff_ID: parseInt(assignStaffId, 10),
        Role: "Driver",
        From_Date: assignFrom,
      })
    )
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
          });
          setShowAssign(false);
          loadVehicles();
        } else {
          toast.error(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(() =>
        toast.error("Network error", { position: toast.POSITION.TOP_RIGHT })
      )
      .finally(() => setAssignSaving(false));
  };

  const deleteVehicle = (vehicle) => {
    if (
      !window.confirm(
        `Delete vehicle ${vehicle.Registration_No}? Its fuel and maintenance history is kept.`
      )
    ) {
      return;
    }
    post("Vehicle/DeleteVehicle", auth({ ID: vehicle.Vehicle_ID }))
      .then((res) => {
        if (res.data.status_code === 1) {
          toast.success(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
          });
          loadVehicles();
        } else {
          // Most often: routes still point at this vehicle. The message says which.
          toast.error(res.data.status_message, {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 8000,
          });
        }
      })
      .catch(() =>
        toast.error("Network error", { position: toast.POSITION.TOP_RIGHT })
      );
  };

  const totals = vehicles.reduce(
    (acc, v) => ({
      routes: acc.routes + (v.RouteCount || 0),
      students: acc.students + (v.StudentCount || 0),
      seats: acc.seats + (v.Capacity || 0),
      noDriver: acc.noDriver + (v.CurrentDriver ? 0 : 1),
    }),
    { routes: 0, students: 0, seats: 0, noDriver: 0 }
  );

  const renderRows = () => {
    if (vehicles.length === 0) {
      return (
        <tr>
          <td colSpan="9" className="text-center text-muted py-4">
            No vehicles found
          </td>
        </tr>
      );
    }

    return vehicles.map((v, i) => {
      // More students than seats is not necessarily wrong - a bus can run several
      // trips - but it is worth showing so it can be checked.
      const overCapacity =
        v.Capacity > 0 && v.StudentCount > v.Capacity ? true : false;

      return (
        <tr key={v.Vehicle_ID}>
          <td>{i + 1}</td>
          <td className="fw-bold">{v.Registration_No}</td>
          <td>{v.Model || "—"}</td>
          <td className="text-end">{v.Capacity ?? "—"}</td>
          <td className="text-end">{v.RouteCount}</td>
          <td className="text-end">
            {v.StudentCount}
            {overCapacity ? (
              <span
                className="badge bg-warning text-dark ms-2"
                title={`${v.StudentCount} students against ${v.Capacity} seats — check if this bus runs multiple trips`}
              >
                over seats
              </span>
            ) : null}
          </td>
          <td>
            {v.CurrentDriver ? (
              v.CurrentDriver
            ) : (
              <span className="text-muted fst-italic">not assigned</span>
            )}
          </td>
          <td>
            <span
              className={`badge ${
                v.Is_Active ? "bg-success" : "bg-secondary"
              }`}
            >
              {v.Status || (v.Is_Active ? "Active" : "Inactive")}
            </span>
          </td>
          <td>
            <ul className="inline-action" aria-labelledby="dropdownMenuButton1">
              <li>
                <NavLink
                  to={`/Transport/EditVehicle/${v.Vehicle_ID}`}
                  className=" green"
                  title="Edit vehicle"
                >
                  <i className="ri-edit-line"></i>
                </NavLink>
              </li>
              <li>
                <button
                  type="button"
                  className="blue"
                  title="Assign driver"
                  onClick={() => openAssign(v)}
                >
                  <i className="ri-user-line"></i>
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="red"
                  title="Delete vehicle"
                  onClick={() => deleteVehicle(v)}
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </li>
            </ul>
          </td>
        </tr>
      );
    });
  };

  return (
    <>
      <TransportMenu />
      <ToastContainer />

      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-6">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/Dashboard">Dashboard</NavLink>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/Transport/TransportDashboard">Transport</NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Vehicles
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Vehicles (Fleet)</div>
                <p className="text-muted small mb-0">
                  One row per physical bus. Routes point at a vehicle, so the same bus can
                  serve several routes without its details being repeated.
                </p>
              </div>
              <div className="col-md-6">
                <div className="page-right-actions">
                  <NavLink
                    to="/Transport/AddVehicle"
                    className="button button-white button-icon"
                  >
                    Add Vehicle
                    <i className="ri-add-line"></i>
                  </NavLink>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="container">
            {/* summary */}
            <div className="row g-3 mb-3">
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Vehicles</div>
                    <div className="h4 mb-0">{vehicles.length}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Routes served</div>
                    <div className="h4 mb-0">{totals.routes}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Students carried</div>
                    <div className="h4 mb-0">{totals.students}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body py-2">
                    <div className="text-muted small">Without a driver</div>
                    <div
                      className={`h4 mb-0 ${
                        totals.noDriver > 0 ? "text-warning" : ""
                      }`}
                    >
                      {totals.noDriver}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="row g-2 mb-3 align-items-end">
                  <div className="col-md-4">
                    <label className="form-label small mb-1">Search</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Registration, model or area"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <Form.Check
                      type="checkbox"
                      id="activeOnly"
                      label="Active vehicles only"
                      checked={activeOnly}
                      onChange={(e) => setActiveOnly(e.target.checked)}
                    />
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-theme">
                    <thead>
                      <tr>
                        <th>Sr.</th>
                        <th>Registration</th>
                        <th>Model</th>
                        <th className="text-end">Seats</th>
                        <th className="text-end">Routes</th>
                        <th className="text-end">Students</th>
                        <th>Current Driver</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="9" className="text-center py-4">
                            <Spinner animation="border" role="status" />
                          </td>
                        </tr>
                      ) : (
                        renderRows()
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* assign a driver */}
      <Modal
        className="modal-theme"
        show={showAssign}
        onHide={() => setShowAssign(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "18px" }}>
            Assign driver — {assignVehicle?.Registration_No}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted">
            Drivers come from the staff records, so payroll, attendance and leave stay in
            one place. Whoever holds this bus now is closed off the day before the date
            below, keeping the history intact.
          </p>
          <div className="mb-3">
            <label className="form-label">Driver</label>
            <select
              className="form-select"
              value={assignStaffId}
              onChange={(e) => setAssignStaffId(e.target.value)}
            >
              <option value="">-- select --</option>
              {drivers.map((d) => (
                <option key={d.Staff_ID} value={d.Staff_ID}>
                  {d.StaffName} ({d.Designation})
                  {d.Registration_No ? ` — currently on ${d.Registration_No}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Effective from</label>
            <input
              type="date"
              className="form-control"
              value={assignFrom}
              onChange={(e) => setAssignFrom(e.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="button button-light"
            onClick={() => setShowAssign(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="button button-primary"
            onClick={saveAssignment}
            disabled={assignSaving}
          >
            {assignSaving ? "Saving…" : "Assign"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ManageVehicles;
